import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
export interface CaptchaHandle {
  isValid: () => boolean;
  reset: () => void;
}
interface CaptchaProps {
  onValidChange?: (valid: boolean) => void;
}
function generateChallenge() {
  const a = Math.floor(Math.random() * 8) + 2; // 2..9
  const b = Math.floor(Math.random() * 8) + 2; // 2..9
  const ops: Array<'+' | '-' | '×'> = ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let answer = 0;
  let left = a;
  let right = b;
  if (op === '+') answer = a + b;
  if (op === '-') {
    // Keep result non-negative
    left = Math.max(a, b);
    right = Math.min(a, b);
    answer = left - right;
  }
  if (op === '×') answer = a * b;
  return {
    left,
    right,
    op,
    answer
  };
}
export const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({
  onValidChange
}, ref) => {
  const [challenge, setChallenge] = useState(generateChallenge);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const isValid = value.trim() !== '' && Number(value) === challenge.answer;
  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);
  useImperativeHandle(ref, () => ({
    isValid: () => isValid,
    reset: () => {
      setChallenge(generateChallenge());
      setValue('');
      setTouched(false);
    }
  }));
  const refresh = () => {
    setChallenge(generateChallenge());
    setValue('');
    setTouched(false);
  };
  const showError = touched && value !== '' && !isValid;
  return <div data-id="element-118">
        <label htmlFor="captcha-answer" className="block text-sm font-bold text-ink mb-2 uppercase tracking-wide" data-id="element-119">
          <span className="inline-flex items-center gap-2" data-id="element-120">
            <ShieldCheck className="w-4 h-4 text-storm-red" data-id="element-121" />
            Verify You're Human
          </span>
        </label>
        <div className="flex items-stretch gap-2" data-id="element-122">
          <div className="flex items-center justify-center px-4 py-3 rounded-xl border border-subtle-gray bg-off-white font-display text-xl text-ink select-none min-w-[110px]" aria-label={`What is ${challenge.left} ${challenge.op} ${challenge.right}?`} data-id="element-123">
            {challenge.left} {challenge.op} {challenge.right} =
          </div>
          <input type="text" inputMode="numeric" pattern="-?[0-9]*" id="captcha-answer" name="captcha-answer" required autoComplete="off" value={value} onChange={e => setValue(e.target.value)} onBlur={() => setTouched(true)} aria-invalid={showError} aria-describedby="captcha-help" className={`flex-1 min-w-0 px-4 py-3 rounded-xl border bg-off-white focus:outline-none focus:ring-2 focus:ring-storm-red focus:border-transparent transition-all ${showError ? 'border-storm-red' : isValid ? 'border-green-600' : 'border-subtle-gray'}`} placeholder="Answer" data-id="element-124" />
          <button type="button" onClick={refresh} aria-label="Get a new captcha challenge" className="px-3 py-3 rounded-xl border border-subtle-gray bg-off-white hover:bg-subtle-gray transition-colors shrink-0" data-id="element-125">
            <RefreshCw className="w-4 h-4 text-gray-warm" data-id="element-126" />
          </button>
        </div>
        <p id="captcha-help" className={`text-xs mt-2 ${showError ? 'text-storm-red font-bold' : 'text-gray-warm'}`} data-id="element-127">
          {showError ? 'Incorrect answer. Please try again or refresh the challenge.' : "Solve the math problem above to confirm you're not a bot."}
        </p>
      </div>;
});
Captcha.displayName = 'Captcha';